import ModalWindow from '../../common/Modal/ModalWindow';
import InputNumber from 'rc-input-number';
import Button from '../../common/Button';
import styles from './BulkEditInventoryQuantitiesModal.module.scss';
import Select from '../../common/Select';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { postVariantQuantities } from '../../../store/actions/variant-bulk-edit';
import InventoryWarnings from './InventoryWarnings';
import { editInvetoryQuantitiesUtils as utils } from '../../../utils/editInventoryQuantities';
import { reportBusinessActionToMixpanel } from '../../../utils/reportBusinessActionToMixpanel';
import { useMixpanel } from 'react-mixpanel-browser';
import { UPDATE_INVENTORY_VIA_POPUP } from '../../../constants/mixpanel';

export default function BulkEditInventoryQuantitiesModal({
  isOpen,
  onClose,
  warehouses = [],
  variantIds = [],
  variantsWithDetails = [],
  fetchInventory,
}) {
  const mixpanel = useMixpanel();
  const { bulkEdit } = useSelector((state) => state.supplierInventory);
  const { supplier, user } = useSelector((state) => state.role);

  const [formData, setFormData] = useState({
    variantIds,
    quantity: null,
    warehouseId: warehouses?.[0]?.id || null,
  });

  const [inputValidationWarnings, setInputValidationWarnings] = useState([]);

  useEffect(() => {
    if (!variantIds.length) {
      onClose();
    }
    setFormData((formData) => ({ ...formData, variantIds }));
  }, [variantIds, onClose]);

  const dispatch = useDispatch();

  useEffect(() => {
    if (bulkEdit.success) {
      onClose();
    }
  }, [bulkEdit, dispatch, onClose]);

  async function handleSubmit(e) {
    e.preventDefault();

    reportBusinessActionToMixpanel(mixpanel, UPDATE_INVENTORY_VIA_POPUP, {
      supplierName: supplier.name,
      userEmail: user.email,
    });

    const payload = {
      quantity: Number(formData.quantity),
      warehouseId: Number(formData.warehouseId),
      variantIds: formData.variantIds,
    };

    dispatch(postVariantQuantities(payload, fetchInventory));
  }

  const onWarehouseChange = (warehouseId) => {
    setFormData((formData) => ({ ...formData, warehouseId }));
  };

  const onQuantityChange = (quantity) => {
    setInputValidationWarnings(utils.getQuantityValidationWarnings(quantity));

    setFormData((formData) => ({ ...formData, quantity }));
  };

  return (
    <ModalWindow open={isOpen} onClose={onClose} size="medium" center>
      <form onSubmit={handleSubmit}>
        <div className={styles.header}>
          <h3>Edit inventory</h3>
          <p>Set total units value for selected SKUs</p>
        </div>

        <div className={styles.quantitySection}>
          <section>
            <div className={styles.selectedItems}>
              <div className={styles.selectedItemsHeader}>
                <p className={styles.countBox}>{variantIds.length}</p>
                <div className={styles.selectedItemsText}>
                  <p>
                    Editing total units for <span className={styles.totalUnitCount}>{variantIds.length} SKUs</span>
                  </p>
                </div>
              </div>
              <InventoryWarnings variantsWithDetails={variantsWithDetails} selectedQuantity={formData.quantity} />
            </div>

            <div className={styles.inputGroup}>
              <label>Units per SKU</label>
              <InputNumber
                value={formData.quantity}
                onChange={onQuantityChange}
                type="number"
                placeholder="Enter value"
                required
                className={styles.quantityInput}
                precision={0}
                step={1}
                min={0}
              />
            </div>
          </section>

          {warehouses.length > 1 &&
            renderWarehouseRadioGroupSection({
              warehouses,
              onWarehouseChange,
              warehouseId: formData.warehouseId,
            })}

          {warehouses.length === 1 && renderSingleWarehouseSection({ warehouse: warehouses[0] })}

          <p className={styles.warning}>
            By editing the total units value, you will override the automatically imported data for this field across
            all selected SKUs. Any subsequent inventory updates will override the value set here.
          </p>
        </div>

        <div className={styles.actions}>
          <Button text="Cancel" onClick={onClose} color="white" disabled={bulkEdit.isLoading} />
          <Button
            text="Confirm changes"
            loading={bulkEdit.isLoading}
            type="submit"
            disabled={inputValidationWarnings.length > 0 || bulkEdit.isLoading}
          />
        </div>
      </form>
    </ModalWindow>
  );
}

const renderWarehouseRadioGroupSection = ({ warehouses, onWarehouseChange, warehouseId }) => {
  return (
    <section className={styles.warehouseSection}>
      <div>
        <h3>Warehouses</h3>
        <p>Select a warehouse to allocate the inventory for the selected products</p>
      </div>
      <div className={styles.selectContainer}>
        <Select
          options={warehouses.map((warehouse) => ({ label: warehouse.name, value: warehouse.id }))}
          onChange={onWarehouseChange}
          value={String(warehouseId)}
        />
      </div>
    </section>
  );
};

const renderSingleWarehouseSection = ({ warehouse }) => {
  return (
    <section className={styles.warehouseSection}>
      <div>
        <h3>Warehouses</h3>
        <p>Cymbio will update this warehouse:</p>
      </div>
      <div className={styles.singleWarehouseLabel}>
        <p>{warehouse.name}</p>
      </div>
    </section>
  );
};
